import type { Breadcrumb } from '@sentry/types';

/**
 * Takes the identifiers out of a breadcrumb before Sentry keeps it.
 *
 * WHAT WAS LEAVING THE PHONE. Found by the Security & infra vertical on
 * 16 October 2026, reading the Sentry project (Asana 1219575979076270): the
 * trail attached to every mobile error carried, in clear, group and activity
 * ObjectIds in the navigation routes (/home/group/<id>) and in the API calls
 * (/api/v1/groups/<id>), the FCM push token (/users/renew-push-token/<token>),
 * and the socket.io session ids. The sign-up route takes an email in its path
 * (sign-up/:email), so an email could travel the same way; that one was
 * deduced from the code, never observed. Nothing filtered any of it.
 *
 * WHAT IS KEPT. The shape of the trail: which screen, which endpoint, which
 * status. That is what makes a crash readable, and it never needed the ids.
 *
 * WHAT IS NOT COVERED. Breadcrumbs the native SDKs record on their own side
 * do not pass through this JavaScript hook.
 *
 * NO LOOKBEHIND in these patterns, on purpose: the application still supports
 * iOS 15, whose WebView rejects a lookbehind with a SyntaxError, and this file is
 * loaded by the Sentry set-up at the very first tick. A test refuses it.
 */

const EMAIL = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PUSH_TOKEN = /(renew-push-token\/)[^/?#\s]+/gi;
const SECRET_PARAMETER = /([?&](?:sid|token|access_token|id_token|refresh_token|oobCode|apiKey|key|email)=)[^&#\s]*/gi;
const OBJECT_ID = /\b[0-9a-f]{24}\b/gi;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
// A path segment that long is a token whatever its route: no screen or
// endpoint of ours is named with forty characters.
const LONG_SEGMENT = /(\/)[A-Za-z0-9_:.-]{40,}(?=[/?#\s]|$)/g;

export const nameNoOne = (text: string): string =>
  text
    .replace(EMAIL, ':email')
    .replace(PUSH_TOKEN, '$1:token')
    .replace(SECRET_PARAMETER, '$1:redacted')
    .replace(UUID, ':id')
    .replace(OBJECT_ID, ':id')
    .replace(LONG_SEGMENT, '$1:token');

const scrub = (value: unknown): unknown => (typeof value === 'string' ? nameNoOne(value) : value);

/**
 * Every string of the breadcrumb, not only the fields known today: navigation
 * puts the route in `from` and `to`, xhr and fetch put the address in `url`,
 * and a console breadcrumb carries whatever was logged in `message`.
 */
export const aBreadcrumbNamesNoOne = (crumb: Breadcrumb): Breadcrumb => ({
  ...crumb,
  ...(typeof crumb.message === 'string' ? { message: nameNoOne(crumb.message) } : {}),
  ...(crumb.data
    ? { data: Object.fromEntries(Object.entries(crumb.data).map(([key, value]) => [key, scrub(value)])) }
    : {}),
});
